import React, { useEffect, useRef, useState } from "react";
import {
  formatToLocalDate,
  getAntdSortOrder,
  rangePickerProps,
  rangePresets,
  ROLE,
  useDebounce,
} from "../../../helpers/utils";
import { svg } from "../../../Assets/svg";
import {
  DatePicker,
  Dropdown,
  Menu,
  Spin,
  Table,
  TableColumnsType,
} from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import CreateSubAdminModal from "./Components/CreateSubAdminModal";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import { deleteSubAdminAPI, getSubAdminListAPI } from "./subAdminApis";
import FullScreenLoader from "../../Components/FullScreenLoader";
import NoDataMessage from "../../Components/NoData";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  decryptByText,
  getDecryptedName,
  getDecryptedUserData,
} from "../../../helpers/bycrypt";
import { CommonConfirmationModal } from "../../../Components/CommonConfirmationModal";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import NoPermissionPage from "../../Components/NoPermissionPage";
import NotificationButton from "../../Components/NotificationButton";
import { Capacitor } from "@capacitor/core";

dayjs.extend(utc);
const { RangePicker }: any = DatePicker;

const SubAdmins = () => {
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData.SUB_ADMINS,
  );
  const { authLoading } = useAppSelector((state: RootState) => state.loading);

  const dispatch = useAppDispatch();
  const userData = getDecryptedUserData();
  console.log(userData, "userDatauserDatauserDatauserData");
  const [loading, setLoading] = useState(false);
  const [subAdminsList, setSubAdminsList] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedSubAdmin, setSelectedSubAdmin] = useState<any>(null);
  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Pagination & Filters
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState(true);
  const [limit] = useState(30);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [sorterState, setSorterState] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>({ field: null, order: null });

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const searchBarHandler = (e: any) => setSearch(e.target.value);
  const searchBarClean = () => setSearch("");

  const onRangeChange = (dates: any) => {
    if (dates) {
      setStartDate(dates[0]?.format("YYYY-MM-DD"));
      setEndDate(dates[1]?.format("YYYY-MM-DD"));
    } else {
      setStartDate("");
      setEndDate("");
    }
  };

  const toggleFilter = () => {
    setIsFilterOpen((prevState) => !prevState);
  };

  const fetchSubAdmins = async (page: number, isReset: boolean) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (isReset) {
      setLoading(true);
    }

    const filters = {
      page: page,
      limit: limit,
      search: debouncedSearch,
      created_from: startDate,
      created_to: endDate,
      sortBy: sorterState.field,
      sortOrder: sorterState.order,
    };

    dispatch(
      getSubAdminListAPI({
        filters,
        signal: controller.signal,
        onSuccess: (data: any) => {
          const newData = data?.data || [];
          const total = data?.total || 0;
          setTotalCount(total);

          if (isReset) {
            setSubAdminsList(newData);
            window.scrollTo(0, 0);
          } else {
            setSubAdminsList((prev) => [...prev, ...newData]);
          }

          const currentCount = isReset
            ? newData.length
            : subAdminsList.length + newData.length;
          setHasMore(currentCount < total);
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchSubAdmins(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, startDate, endDate, sorterState]);

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchSubAdmins(nextPage, false);
  };

  const handleEditClick = (record: any) => {
    setSelectedSubAdmin(record);
    setIsAddModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!idToDelete) return;

    setDeleting(true);

    dispatch(
      deleteSubAdminAPI({
        id: idToDelete,
        onSuccess: () => {
          setDeleting(false);
          setIdToDelete(null);
          fetchSubAdmins(1, true);
        },
        onError: () => {
          setDeleting(false);
        },
      }),
    );
  };

  const handleCloseDeleteModal = () => {
    setIdToDelete(null);
  };

  const MoreOptionsMenu = (data: any) => {
    return (
      <Menu>
        {USER_PERMISSIONS.MODIFY && (
          <Menu.Item key="1" onClick={() => handleEditClick(data)}>
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              Modify
            </div>
          </Menu.Item>
        )}
        {/* ✅ 4. Remove Guard */}
        {USER_PERMISSIONS.REMOVE && (
          <Menu.Item key="2" onClick={() => setIdToDelete(data._id)}>
            <div className="text-danger d-flex align-items-center gap-2 svg-20">
              Remove
            </div>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    console.log("handleTableChange", sorter);
    // Convert AntD 'ascend'/'descend' to Backend 'asc'/'desc'
    const order =
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
        ? "desc"
        : null;
    setSorterState({ field: sorter.field, order: order as any });
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: getAntdSortOrder("name", sorterState),
      // render: (name: string, record: any) => (
      //   <div className="d-flex align-items-center">{record.first_name}</div>
      // ),
      render: (val: any, record: any) => getDecryptedName(record),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
      sortOrder: getAntdSortOrder("email", sorterState),
      // render: (name: string, record: any) => (
      //   <div className="d-flex align-items-center">{record.first_name}</div>
      // ),
      render: (val: any, record: any) => decryptByText(val),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      sorter: true,
      sortOrder: getAntdSortOrder("role", sorterState),
      // render: (name: string, record: any) => (
      //   <div className="d-flex align-items-center">{record.first_name}</div>
      // ),
      render: (val: any, record: any) => {
        return (
          <span
            className={
              "d-inline-flex align-items-center billStatus text-nowrap " +
              `${
                record?.roleData?.key == ROLE?.ADMIN
                  ? "approvedStatus"
                  : "designPendingStatus"
              }`
            }
          >
            {record?.roleData?.name}
          </span>
        );
      },
    },
    {
      title: "Created By",
      dataIndex: "created_by",
      key: "created_by",
      sorter: true,
      sortOrder: getAntdSortOrder("created_by", sorterState),
      render: (val: any) => getDecryptedName(val),
    },
    {
      title: "Created On",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      sortOrder: getAntdSortOrder("created_at", sorterState),
      render: (date: string) => formatToLocalDate(date, true),
    },
    {
      title: "Action",
      key: "action",
      width: "1%",

      render: (_, record) => (
        <div className="d-flex" onClick={(e) => e.stopPropagation()}>
          {record?.roleData?.key !== ROLE?.ADMIN ? (
            <Dropdown
              overlay={() => MoreOptionsMenu(record)}
              trigger={["click"]}
              placement="bottomRight"
              className="cursor-pointer"
              overlayClassName="moreOptionDropdown"
            >
              <span className="svg-32 svg-outline-primary" role="button">
                {svg?.tableMoreOption}
              </span>
            </Dropdown>
          ) : (
            ""
          )}
        </div>
      ),
    },
  ];

  return USER_PERMISSIONS.READ ? (
    <>
      <div className="page-content h-100 px-40 py-40">
        <div className="mb-sm-3 mb-2 d-flex flex-column gap-md-2 gap-0">
          <div className="d-flex flex-md-nowrap flex-wrap gap-sm-3 gap-2 justify-content-between align-items-center">
            <h2 className="text-black fs-36 fw-bolder page-title mb-0 order-md-1 order-1">
              Manage Admins ({totalCount})
            </h2>
            <div
              className={`d-flex flex-md-nowrap flex-wrap gap-sm-3 gap-2 justify-content-end align-items-center ms-auto order-md-2 order-4 mobileFilter ${
                isFilterOpen ? "is-open" : ""
              }`}
            >
              <div className="pageSearch mw-100 position-relative d-inline-block mt-sm-0 mt-2 mob-w-100">
                <input
                  type="text"
                  value={search}
                  className="form-control form-control-lg"
                  onChange={searchBarHandler}
                  placeholder="Search"
                />
                {search?.length ? (
                  <span className="cursor-pointer" onClick={searchBarClean}>
                    {svg?.searchClear}
                  </span>
                ) : (
                  <span>{svg?.search}</span>
                )}
              </div>

              <div className="dateSelectFilter RangeDatePicker serviceDatePicker border-s mw-100 d-inline-block p-0">
                <RangePicker
                  presets={rangePresets}
                  onChange={onRangeChange}
                  {...rangePickerProps}
                  value={
                    startDate && endDate
                      ? [dayjs(startDate), dayjs(endDate)]
                      : null
                  }
                  inputReadOnly={Capacitor.isNativePlatform()}
                />
              </div>
            </div>
            {USER_PERMISSIONS.ADD && (
              <button
                className="btn btn-lg btn-primary mob-btn order-md-3 order-2 ms-md-0 ms-auto"
                onClick={() => {
                  setSelectedSubAdmin(null);
                  setIsAddModalOpen(true);
                }}
              >
                + CREATE
              </button>
            )}
            <div className="d-flex gap-sm-3 gap-2  order-md-3 order-3">
              <div
                onClick={toggleFilter}
                role="button"
                aria-expanded={isFilterOpen}
                className="filterButton d-sm-none"
              >
                {svg?.filterIcon}
              </div>
              <NotificationButton />
            </div>
          </div>
        </div>

        <div className="card page-table-card border-0 shadow rounded-4 flex-fill overflow-y-auto">
          <div className="card-body overflow-y-auto" id="scrollableDiv">
            {loading && currentPage === 1 ? (
              <FullScreenLoader />
            ) : subAdminsList.length === 0 ? (
              <NoDataMessage
                icon={svg?.noSubAdminIcon}
                text="You do not have any subadmins yet."
              />
            ) : (
              <InfiniteScroll
                dataLength={subAdminsList.length}
                next={handleLoadMore}
                hasMore={hasMore}
                loader={
                  loading && (
                    <div className="text-center p-2">
                      <Spin />
                    </div>
                  )
                }
                scrollableTarget="scrollableDiv"
              >
                <Table
                  dataSource={subAdminsList}
                  scroll={{ x: "max-content" }}
                  pagination={false}
                  columns={columns}
                  onChange={handleTableChange}
                  rowKey={(record) => record._id}
                  className="custom-table"
                  bordered
                />
              </InfiniteScroll>
            )}
          </div>
        </div>
      </div>

      <CreateSubAdminModal
        open={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
        }}
        subAdminData={selectedSubAdmin}
        refreshData={() => fetchSubAdmins(1, true)}
      />

      <CommonConfirmationModal
        open={idToDelete != null}
        title="Remove Sub Admin, Are You Sure?"
        subTitle="Removing this sub-admin will revoke their access and delete their account from the system. Please confirm before proceeding."
        loading={deleting}
        onSuccess={handleDeleteConfirm}
        onClose={handleCloseDeleteModal}
      />
    </>
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default SubAdmins;
